import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table } from 'antd';
import { FormReassignmentPermissions as FormReassignmentPermissionsLib } from 'ontraccr-common';

import HoverHelp from '../../common/HoverHelp';
import sortByString from '../../helpers/helpers';
import CustomModal from '../../common/modals/CustomModal';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import colors from '../../constants/Colors';
import FormReassignmentPermissionSelector from './FormReassignmentPermissionSelector';

const {
  ACCESS_ASSIGNED,
  ACCESS_ALL,
  ASSIGN_OWN,
  ASSIGN_ALL,
  toEditorRows,
  translateLegacyReassignment,
} = FormReassignmentPermissionsLib;

const ACCESS_PERM_TYPES = [
  { value: ACCESS_ASSIGNED, label: 'Assigned' },
  { value: ACCESS_ALL, label: 'All' },
];

const ASSIGNMENT_PERM_TYPES = [
  { value: ASSIGN_OWN, label: 'Assign Own' },
  { value: ASSIGN_ALL, label: 'Assign All' },
];

const roleColumnCellProps = {
  onCell: () => ({ style: { padding: '10px 16px' } }),
  onHeaderCell: () => ({ style: { padding: '10px 16px' } }),
};

const permColumnCellProps = {
  onCell: () => ({ style: { padding: '10px 20px' } }),
  onHeaderCell: () => ({ style: { padding: '10px 20px' } }),
};

const headerWithHelp = (title, content) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    {title}
    <HoverHelp
      content={(
        <div style={{ maxWidth: 240 }}>
          {content}
        </div>
      )}
    />
  </span>
);

const isCompleteRole = (row) => row.access && row.assignment;

const isIncompleteRole = (row) => (row.access || row.assignment) && !isCompleteRole(row);

export default function FormReassignmentPermissions({
  value,
  onChange,
  allowReassignment,
  legacyReassignAll,
  isDisplay,
}) {
  const positionNames = useSelector((state) => state.settings.positionNames);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [draftRows, setDraftRows] = useState([]);

  const config = useMemo(() => translateLegacyReassignment({
    allowReassignment,
    reassignAll: legacyReassignAll,
    reassignmentPermissions: value,
  }), [allowReassignment, legacyReassignAll, value]);

  const rows = useMemo(
    () => toEditorRows(config, positionNames),
    [config, positionNames],
  );

  const hasIncompleteRows = useMemo(
    () => draftRows.some(isIncompleteRole),
    [draftRows],
  );

  const openModal = useCallback(() => {
    setDraftRows(rows);
    setIsModalVisible(true);
  }, [rows]);

  const closeModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const saveAndClose = useCallback(() => {
    if (hasIncompleteRows) return;

    // If the user hasn't changed anything for legacy Reassign Own virtual display, don't save.
    if (config.isLegacyReassignOwn) {
      const unchanged = draftRows.every((row, index) => {
        const initial = rows[index];
        return row.access === initial?.access
          && row.assignment === initial?.assignment;
      });
      if (unchanged) {
        setIsModalVisible(false);
        return;
      }
    }
    onChange(
      draftRows
        .filter(isCompleteRole)
        .map((row) => ({
          positionId: row.positionId,
          access: row.access,
          assignment: row.assignment,
        })),
    );
    setIsModalVisible(false);
  }, [config.isLegacyReassignOwn, draftRows, hasIncompleteRows, onChange, rows]);

  useEffect(() => {
    if (!isModalVisible) return;
    setDraftRows(rows);
  }, [isModalVisible, rows]);

  const onRowChange = useCallback(({ positionId, permKey, nextValue }) => {
    setDraftRows((currentRows) => currentRows.map((row) => {
      if (row.positionId !== positionId) return row;
      const updated = {
        ...row,
        [permKey]: nextValue || null,
      };
      if (permKey === 'access' && !nextValue) {
        updated.assignment = null;
      }
      return updated;
    }));
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Role',
      dataIndex: 'name',
      key: 'name',
      sorter: sortByString('name'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      ...roleColumnCellProps,
    },
    {
      title: headerWithHelp(
        'Access',
        'Who in this role can reassign the form. Assigned - only users currently assigned to this form. All - any user with this role.',
      ),
      dataIndex: 'access',
      key: 'access',
      width: 220,
      ...permColumnCellProps,
      render: (access, record) => (
        <FormReassignmentPermissionSelector
          value={access}
          options={ACCESS_PERM_TYPES}
          onChange={(nextValue) => onRowChange({
            positionId: record.positionId,
            permKey: 'access',
            nextValue,
          })}
        />
      ),
    },
    {
      title: headerWithHelp(
        'Assignment',
        'Who they can reassign the form to. Assign Own: only themselves. Assign All: any user.',
      ),
      dataIndex: 'assignment',
      key: 'assignment',
      width: 220,
      ...permColumnCellProps,
      render: (assignment, record) => (
        <FormReassignmentPermissionSelector
          value={assignment}
          options={ASSIGNMENT_PERM_TYPES}
          disabled={!record.access}
          onChange={(nextValue) => onRowChange({
            positionId: record.positionId,
            permKey: 'assignment',
            nextValue,
          })}
        />
      ),
    },
  ], [onRowChange]);

  if (!allowReassignment || isDisplay) return null;

  return (
    <div style={{
      width: 500, marginTop: 4, marginBottom: 12, marginLeft: 28,
    }}
    >
      <OnTraccrButton
        title="Configure"
        onClick={openModal}
      />
      <CustomModal
        title="Configure Reassignment Permissions"
        visible={isModalVisible}
        onCloseClicked={closeModal}
        maskColor={colors.MODAL_MASK_DARK}
        width={800}
        destroyOnClose
      >
        <div style={{ padding: '0 24px 8px', width: '100%' }}>
          {config.isLegacyReassignOwn ? (
            <p style={{
              color: colors.GRAY_700,
              fontSize: 13,
              lineHeight: 1.5,
              margin: '0 0 12px',
            }}
            >
              This form uses legacy Reassign Own settings. Assigned users can reassign
              to anyone. Values below are shown for reference until you customize them.
            </p>
          ) : null}
          <div className="file-list-container" style={{ height: 'auto', marginTop: 0, width: '100%' }}>
            <Table
              size="small"
              pagination={false}
              rowKey="positionId"
              columns={columns}
              dataSource={draftRows}
              style={{ width: '100%' }}
              scroll={{ y: 400 }}
              tableLayout="fixed"
              locale={{ emptyText: 'No restrictions' }}
            />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 20,
            paddingBottom: 8,
          }}
          >
            <OnTraccrButton
              title="Done"
              roundStyle
              onClick={saveAndClose}
              disabled={hasIncompleteRows}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
}

FormReassignmentPermissions.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    positionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    access: PropTypes.string,
    assignment: PropTypes.string,
  })),
  onChange: PropTypes.func,
  allowReassignment: PropTypes.bool,
  legacyReassignAll: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
  isDisplay: PropTypes.bool,
};

FormReassignmentPermissions.defaultProps = {
  value: null,
  onChange: () => {},
  allowReassignment: false,
  legacyReassignAll: null,
  isDisplay: false,
};
